import { authorizedFetch } from './api';

/**
 * Ereignisse beim Stöbern im Skill-Katalog (Forschung FF 3, Codebuch § 4.18).
 *
 * Feste Typen ohne Freitext, wie im Backend (`routes/events.js`):
 *   BROWSE_TAB_OPENED     {}
 *   BROWSE_SKILL_OPENED   { skillId }
 *   BROWSE_PROFILE_OPENED { skillId, role: 'offer' | 'seek' }
 *
 * Gepuffert: Ein Tipp erzeugt keinen eigenen Request. Gesendet wird nach
 * FLUSH_DELAY_MS, sobald MAX_BUFFER Ereignisse beisammen sind, oder wenn die
 * Seite in den Hintergrund geht. Immer per fetch(..., { keepalive: true }),
 * NICHT per sendBeacon: sendBeacon kann keinen Authorization-Header setzen,
 * und ohne ihn verwirft der Server alles. keepalive lässt den Request das
 * Schließen der Seite überleben.
 *
 * Fehler bleiben still: Ein verlorenes Ereignis ist kein Grund, Schüler:innen
 * eine Meldung zu zeigen. Suchbegriffe werden nie protokolliert.
 */

export const BROWSE_EVENT_TYPES = ['BROWSE_TAB_OPENED', 'BROWSE_SKILL_OPENED', 'BROWSE_PROFILE_OPENED'];

export const FLUSH_DELAY_MS = 2000;
export const MAX_BUFFER = 10;
// Der Server nimmt höchstens 20 je Aufruf (MAX_EVENTS_PER_CALL).
const MAX_PER_REQUEST = 20;
// Zweimal „Reiter geöffnet“ in dieser Zeit ist ein doppeltes Einhängen
// (React StrictMode, schneller Hin- und Herwechsel), keine zweite Öffnung.
const TAB_OPEN_DEDUPE_MS = 1000;

let buffer = [];
let timer = null;
let lastTabOpenedAt = -Infinity;
let listenersAttached = false;

const send = (events) => {
  try {
    return authorizedFetch('/api/v1/events', {
      method: 'POST',
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => undefined);
  } catch (error) {
    return Promise.resolve();
  }
};

/** Schickt alles, was im Puffer liegt (in Stücken zu höchstens 20). */
export const flushBrowseEvents = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (buffer.length === 0) return Promise.resolve();
  const pending = buffer;
  buffer = [];
  const requests = [];
  for (let i = 0; i < pending.length; i += MAX_PER_REQUEST) {
    requests.push(send(pending.slice(i, i + MAX_PER_REQUEST)));
  }
  return Promise.all(requests).then(() => undefined);
};

const attachListeners = () => {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;
  const flushWhenHidden = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'hidden') flushBrowseEvents();
  };
  document.addEventListener('visibilitychange', flushWhenHidden);
  window.addEventListener('pagehide', () => flushBrowseEvents());
};

/**
 * Merkt ein Ereignis vor. Unbekannte Typen werden ignoriert, Daten auf die
 * bekannten Felder gekürzt (der Server prüft trotzdem selbst).
 */
export const trackBrowseEvent = (type, data = {}) => {
  if (!BROWSE_EVENT_TYPES.includes(type)) return;

  if (type === 'BROWSE_TAB_OPENED') {
    const now = Date.now();
    if (now - lastTabOpenedAt < TAB_OPEN_DEDUPE_MS) return;
    lastTabOpenedAt = now;
  }

  const clean = {};
  if (type !== 'BROWSE_TAB_OPENED' && data.skillId) clean.skillId = String(data.skillId);
  if (type === 'BROWSE_PROFILE_OPENED' && (data.role === 'offer' || data.role === 'seek')) clean.role = data.role;

  buffer.push({ type, data: clean });
  attachListeners();

  if (buffer.length >= MAX_BUFFER) {
    flushBrowseEvents();
  } else if (!timer) {
    timer = setTimeout(flushBrowseEvents, FLUSH_DELAY_MS);
  }
};

/** Nur für Tests: Puffer, Zeitgeber und Entprellung zurücksetzen. */
export const resetBrowseEventsForTests = () => {
  if (timer) clearTimeout(timer);
  timer = null;
  buffer = [];
  lastTabOpenedAt = -Infinity;
};

export default trackBrowseEvent;
